export interface RuntimeFault { kind: string; epsg: number | null; msg: string; fix: string }

/**
 * Rendering faults surface long after the call that caused them — inside OpenLayers'
 * render loop, or an unsettled promise — so no try/catch around our own code sees them.
 * Anything unrecognised is still reported: losing an error silently is worse than a
 * vague message.
 */
let lastFault = '';
let lastFaultAt = 0;
let suppressed = 0;

/**
 * A failing tile can fire the same error hundreds of times in a second. Report the
 * first, then how many followed, rather than flooding the status line with a message
 * the user has already read.
 */
export function throttleFault(f: RuntimeFault): RuntimeFault | null {
  const now = Date.now();
  if (f.msg === lastFault && now - lastFaultAt < 4000) {
    suppressed++;
    return null;
  }
  const extra = suppressed > 0 && f.msg !== lastFault
    ? ` (${suppressed} more like the last one)` : '';
  lastFault = f.msg;
  lastFaultAt = now;
  suppressed = 0;
  return extra ? { ...f, msg: f.msg + extra } : f;
}

export function classifyRuntime(m: unknown): RuntimeFault | null {
  const s = String((m as Error)?.message ?? m ?? '');
  if (!s) return null;
  const proj = /No transform available between EPSG:(\d+)/i.exec(s)
    ?? /Unknown projection|projection.*not.*(found|defined)/i.exec(s);
  if (proj) {
    const epsg = proj[1] ? +proj[1] : null;
    return { kind: 'projection', epsg,
      msg: epsg ? `This file uses EPSG:${epsg}, which the browser has no definition for.`
        : 'This file uses a projection the browser has no definition for.',
      fix: 'The raster read fine — only the map placement failed. Fetch the definition, or reproject with gdalwarp -t_srs EPSG:32610.' };
  }
  if (/shader|WebGL|compil|getProgram|linkProgram/i.test(s)) {
    return { kind: 'shader', epsg: null, msg: 'The GPU rejected the render program.',
      fix: 'Everything in the sidebar was read correctly — this is a rendering fault, not a problem with the file.' };
  }
  if (/decode|codec|Deflate|LZW|JPEG|ZSTD|LERC/i.test(s)) {
    return { kind: 'codec', epsg: null, msg: 'A tile failed to decode: ' + s,
      fix: 'geotiff.js cannot decode every codec. Re-encode with -co COMPRESS=DEFLATE.' };
  }
  if (/Failed to fetch|NetworkError|CORS|Load failed|Request failed/i.test(s)) {
    return { kind: 'network', epsg: null, msg: 'A tile request failed: ' + s,
      fix: 'Usually CORS, or a missing byte-range response part way through a read.' };
  }
  return { kind: 'other', epsg: null, msg: 'Rendering error: ' + s,
    fix: 'This came from the render loop rather than the file read. The console has the stack.' };
}

/** Explains a load failure in terms of what to change. */
export function explainLoadFailure(e: unknown, url: string): { msg: string; fix: string } {
  const m = String((e as Error)?.message ?? e);
  // geotiff.js reports a failed fetch as a bare "Request failed", which says nothing
  // about the actual cause — almost always CORS or a refused range request.
  if (/Failed to fetch|NetworkError|CORS|Load failed|Request failed|ERR_/i.test(m)) {
    return { msg: `Cannot reach ${url || 'the file'}.`,
      fix: 'Almost always CORS. The host must send Access-Control-Allow-Origin and expose Accept-Ranges / Content-Range. Use "Open local file" to rule the network out.' };
  }
  if (/Range|206|partial/i.test(m)) {
    return { msg: 'The server ignored the byte-range request.',
      fix: 'Range support is mandatory for COG reads — most CDNs need it enabled explicitly.' };
  }
  if (/not a (tiff|geotiff)|magic|Invalid/i.test(m)) {
    return { msg: 'This does not parse as a TIFF.',
      fix: 'Check the URL returns the image and not an HTML error page.' };
  }
  return { msg: m, fix: '' };
}
